/* =============================================================================
   vf-public.js — VeriFactu public landing init (vanilla, no jQuery)
   Loaded from resources/js/entries/vf-public.js
   Features:
     - Scroll-reveal via IntersectionObserver (elements with [data-appear])
     - Hero typing effect for [data-vf-typed] with rotating words
     - Code-loop animation for [data-vf-code-loop]
     - CountUp animation for [data-vf-count] elements
     - 3D tilt on [data-tilt] cards
     - Billing toggle (monthly/yearly) on .almvf-toggle__switch
     - AEAT countdown banner [data-vf-countdown="YYYY-MM-DD"]
     - Mobile nav toggle [data-vf-nav-toggle]
     - Nav sticky shadow on scroll
     - FAQ exclusive open (close others when one opens)
   ============================================================================= */

(function () {
    'use strict';

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ---------- Scroll-reveal ----------
    function initScrollReveal() {
        const items = document.querySelectorAll('[data-appear]');
        const staggers = document.querySelectorAll('[data-stagger]');
        if (!items.length && !staggers.length) return;

        // Reduced-motion o sin IntersectionObserver: mostrar todo de inmediato.
        // (Antes este return temprano dejaba los contenedores [data-stagger] —los
        // pasos "Cómo funciona"— en opacity:0 para siempre.)
        if (prefersReduced || !('IntersectionObserver' in window)) {
            items.forEach((el) => el.classList.add('is-revealed'));
            staggers.forEach((el) => el.classList.add('is-revealed'));
            return;
        }

        if (items.length) {
            const io = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const delay = parseInt(entry.target.dataset.appearDelay || '0', 10);
                        setTimeout(() => entry.target.classList.add('is-revealed'), delay);
                        io.unobserve(entry.target);
                    }
                });
            }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
            items.forEach((el) => io.observe(el));
        }

        // Contenedores stagger (pasos): observador propio, INDEPENDIENTE de que
        // existan [data-appear] (antes iba acoplado y se saltaba en algunos casos).
        if (staggers.length) {
            const sIO = new IntersectionObserver((entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add('is-revealed');
                        sIO.unobserve(e.target);
                    }
                });
            }, { threshold: 0.2 });
            staggers.forEach((el) => sIO.observe(el));
        }
    }

    // ---------- Hero typing ----------
    function initHeroTyping() {
        const el = document.querySelector('[data-vf-typed]');
        if (!el) return;
        if (prefersReduced) {
            const items = (el.dataset.vfTyped || '').split('|').filter(Boolean);
            el.textContent = items[0] || '';
            return;
        }
        const words = (el.dataset.vfTyped || '').split('|').filter(Boolean);
        if (!words.length) return;

        let wIdx = 0, cIdx = 0, deleting = false;
        const typeSpeed = 65, deleteSpeed = 35, pauseFull = 1600, pauseEmpty = 350;

        function step() {
            const current = words[wIdx];
            if (!deleting) {
                cIdx++;
                el.textContent = current.slice(0, cIdx);
                if (cIdx >= current.length) {
                    deleting = true;
                    setTimeout(step, pauseFull);
                    return;
                }
            } else {
                cIdx--;
                el.textContent = current.slice(0, cIdx);
                if (cIdx <= 0) {
                    deleting = false;
                    wIdx = (wIdx + 1) % words.length;
                    setTimeout(step, pauseEmpty);
                    return;
                }
            }
            setTimeout(step, deleting ? deleteSpeed : typeSpeed);
        }
        setTimeout(step, 600);
    }

    // ---------- Code loop animation ----------
    function initCodeLoop() {
        const frames = document.querySelectorAll('[data-vf-code-loop]');
        if (!frames.length) return;

        const samples = [
            [
                { c: '<span class="almvf-code-c">// Crear factura via API</span>' },
                { c: '<span class="almvf-code-k">const</span> r = <span class="almvf-code-k">await</span> <span class="almvf-code-p">fetch</span>(' },
                { c: '  <span class="almvf-code-s">"https://almc.es/api/verifactu/v1/invoices"</span>,' },
                { c: '  {' },
                { c: '    <span class="almvf-code-p">method</span>: <span class="almvf-code-s">"POST"</span>,' },
                { c: '    <span class="almvf-code-p">headers</span>: { <span class="almvf-code-s">"X-Api-Key"</span>: KEY },' },
                { c: '    <span class="almvf-code-p">body</span>: <span class="almvf-code-p">JSON</span>.<span class="almvf-code-p">stringify</span>({' },
                { c: '      <span class="almvf-code-p">customer</span>: { <span class="almvf-code-p">nif</span>: <span class="almvf-code-s">"B12345678"</span> },' },
                { c: '      <span class="almvf-code-p">total_cents</span>: <span class="almvf-code-n">14500</span>' },
                { c: '    })' },
                { c: '  });' },
                { c: '' },
                { c: '<span class="almvf-code-ok">// → 200 OK  factura aceptada por AEAT</span>' },
                { c: '<span class="almvf-code-c">// hash: a3f2c9...  seq: 124</span>' },
            ],
            [
                { c: '<span class="almvf-code-c"># Stripe Checkout → VeriFactu</span>' },
                { c: '<span class="almvf-code-k">use</span> <span class="almvf-code-bg">ALMC\\VeriFactu\\Client</span>;' },
                { c: '' },
                { c: '<span class="almvf-code-k">$vf</span> = <span class="almvf-code-k">new</span> <span class="almvf-code-bg">Client</span>(<span class="almvf-code-s">$apiKey</span>);' },
                { c: '<span class="almvf-code-k">$invoice</span> = <span class="almvf-code-k">$vf</span>-><span class="almvf-code-p">createFromStripe</span>(' },
                { c: '    <span class="almvf-code-k">$session</span>,' },
                { c: '    [<span class="almvf-code-s">"series"</span> => <span class="almvf-code-s">"F2026"</span>]' },
                { c: ');' },
                { c: '' },
                { c: '<span class="almvf-code-ok">// firmada + enviada AEAT</span>' },
                { c: '<span class="almvf-code-c">// PDF disponible en $invoice->pdf_url</span>' },
            ],
        ];

        // Walk recursively el HTML parseado y construye una lista plana de
        // operaciones { node, char } donde node es el text node destino vacío.
        // Esto nos permite typear carácter a carácter respetando los <span>
        // de syntax highlight sin tener que reparsear.
        function buildTypingOps(srcRoot, dstRoot, ops) {
            srcRoot.childNodes.forEach((child) => {
                if (child.nodeType === Node.TEXT_NODE) {
                    const txt = document.createTextNode('');
                    dstRoot.appendChild(txt);
                    for (const ch of child.textContent) {
                        ops.push({ node: txt, char: ch });
                    }
                } else if (child.nodeType === Node.ELEMENT_NODE) {
                    const clone = child.cloneNode(false); // shell vacío
                    dstRoot.appendChild(clone);
                    buildTypingOps(child, clone, ops);
                }
            });
        }

        // Delay variable según carácter para que parezca persona escribiendo.
        // Si el usuario tiene prefers-reduced-motion: reduce, typeamos pero
        // 2× más rápido para reducir el tiempo total sin perder el efecto
        // (el typing es feature central del hero, no animación decorativa).
        const reducedFactor = prefersReduced ? 0.5 : 1;
        function charDelay(ch) {
            let base;
            if (ch === ' ' || ch === '\t') base = 12 + Math.random() * 8;
            else if (',;'.includes(ch))    base = 55 + Math.random() * 30;
            else if ('{}()[]'.includes(ch))base = 35 + Math.random() * 20;
            else if ('.:'.includes(ch))    base = 22 + Math.random() * 14;
            else                           base = 16 + Math.random() * 14;
            return base * reducedFactor;
        }

        frames.forEach((frame, idx) => {
            const body = frame.querySelector('.almvf-codeframe__body');
            if (!body) return;
            let sampleIdx = idx % samples.length;
            let runId = 0;

            // ── Render con typing carácter a carácter ──
            function renderSample() {
                const myRun = ++runId;
                body.innerHTML = '';
                const lines = samples[sampleIdx];

                // 1) Construir estructura DOM vacía + lista de ops de typing
                const ops = [];
                const lineEls = [];
                lines.forEach((ln) => {
                    const lineEl = document.createElement('span');
                    lineEl.className = 'ln';
                    body.appendChild(lineEl);
                    lineEls.push(lineEl);

                    if (!ln.c) {
                        // línea vacía → nbsp y pausa de fin de línea
                        lineEl.innerHTML = '&nbsp;';
                        ops.push({ pause: 'eol' });
                        return;
                    }
                    const tpl = document.createElement('template');
                    tpl.innerHTML = ln.c;
                    buildTypingOps(tpl.content, lineEl, ops);
                    ops.push({ pause: 'eol' });
                });

                // 2) Cursor parpadeante en la línea activa
                const cursor = document.createElement('span');
                cursor.className = 'almvf-code-cursor';
                cursor.textContent = '▍';
                lineEls[0].appendChild(cursor);

                // 3) Type loop
                let i = 0;
                function typeNext() {
                    if (myRun !== runId) return;          // sample rotated → cancel
                    if (i >= ops.length) {
                        // Done — cursor sigue parpadeando al final
                        return;
                    }
                    const op = ops[i++];
                    let delay;
                    if (op.pause === 'eol') {
                        // mover cursor a la siguiente línea (si existe)
                        const lineIdx = ops.slice(0, i).filter((o) => o.pause === 'eol').length;
                        if (lineIdx < lineEls.length) {
                            lineEls[lineIdx].appendChild(cursor);
                        }
                        delay = (80 + Math.random() * 60) * reducedFactor;
                    } else {
                        op.node.textContent += op.char;
                        delay = charDelay(op.char);
                    }
                    setTimeout(typeNext, delay);
                }
                typeNext();
            }

            // First render
            renderSample();

            // Rotate cada ~14s (typing ~6-9s + lectura ~5s). Aún respeta
            // reduce-motion para la rotación entre samples (es decorativa).
            if (!prefersReduced) {
                setInterval(() => {
                    sampleIdx = (sampleIdx + 1) % samples.length;
                    body.style.transition = 'opacity .4s';
                    body.style.opacity = '0';
                    setTimeout(() => {
                        renderSample();
                        body.style.opacity = '1';
                    }, 420);
                }, 14000);
            }
        });
    }

    // ---------- CountUp ----------
    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
    function initCountUp() {
        const items = document.querySelectorAll('[data-vf-count]');
        if (!items.length) return;
        if (prefersReduced || !('IntersectionObserver' in window)) {
            items.forEach((el) => { el.textContent = formatNum(parseFloat(el.dataset.vfCount), el); });
            return;
        }

        function formatNum(value, el) {
            const decimals = parseInt(el.dataset.vfCountDecimals || '0', 10);
            const suffix = el.dataset.vfCountSuffix || '';
            const prefix = el.dataset.vfCountPrefix || '';
            const fmt = new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
            return prefix + fmt.format(value) + suffix;
        }

        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const el = entry.target;
                const target = parseFloat(el.dataset.vfCount);
                const duration = parseInt(el.dataset.vfCountDuration || '1800', 10);
                const start = performance.now();
                function tick(now) {
                    const t = Math.min(1, (now - start) / duration);
                    const v = target * easeOutCubic(t);
                    el.textContent = formatNum(v, el);
                    if (t < 1) requestAnimationFrame(tick);
                    else el.textContent = formatNum(target, el);
                }
                requestAnimationFrame(tick);
                io.unobserve(el);
            });
        }, { threshold: 0.4 });
        items.forEach((el) => io.observe(el));
    }

    // ---------- 3D Tilt (vanilla) ----------
    function initTilt() {
        if (prefersReduced) return;
        const items = document.querySelectorAll('[data-tilt]');
        if (!items.length) return;
        const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        if (!supportsHover) return;

        items.forEach((el) => {
            const maxTilt = parseFloat(el.dataset.tiltMax || '7');
            let raf = null;

            function update(e) {
                const rect = el.getBoundingClientRect();
                const x = (e.clientX - rect.left) / rect.width;
                const y = (e.clientY - rect.top) / rect.height;
                const ry = (x - 0.5) * maxTilt * 2;
                const rx = (0.5 - y) * maxTilt * 2;
                if (raf) cancelAnimationFrame(raf);
                raf = requestAnimationFrame(() => {
                    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
                });
            }
            function reset() {
                if (raf) cancelAnimationFrame(raf);
                el.style.transition = 'transform .4s cubic-bezier(.22,1,.36,1)';
                el.style.transform = '';
                setTimeout(() => { el.style.transition = ''; }, 420);
            }
            el.addEventListener('mousemove', update);
            el.addEventListener('mouseleave', reset);
        });
    }

    // ---------- Billing toggle ----------
    function initBillingToggle() {
        const sw = document.querySelector('[data-vf-billing-toggle]');
        if (!sw) return;
        const containerSel = sw.dataset.vfBillingTarget || '#planCards';
        const container = document.querySelector(containerSel);
        const lblM = document.querySelector('[data-vf-billing-monthly]');
        const lblY = document.querySelector('[data-vf-billing-yearly]');
        if (!container) return;

        function toggle() {
            const isYearly = container.classList.toggle('is-yearly');
            sw.classList.toggle('is-active', isYearly);
            sw.setAttribute('aria-checked', String(isYearly));
            if (lblM) lblM.classList.toggle('almvf-toggle__label--active', !isYearly);
            if (lblY) lblY.classList.toggle('almvf-toggle__label--active', isYearly);
        }
        sw.addEventListener('click', toggle);
        sw.addEventListener('keydown', function (e) {
            if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); }
        });
        if (lblM) lblM.addEventListener('click', () => { if (container.classList.contains('is-yearly')) toggle(); });
        if (lblY) lblY.addEventListener('click', () => { if (!container.classList.contains('is-yearly')) toggle(); });
    }

    // ---------- AEAT Countdown ----------
    function initAeatCountdown() {
        const el = document.querySelector('[data-vf-countdown]');
        if (!el) return;
        const target = new Date(el.dataset.vfCountdown + 'T00:00:00').getTime();
        if (isNaN(target)) return;
        const out = el.querySelector('.almvf-urgency__countdown') || el;

        function pad(n) { return String(n).padStart(2, '0'); }
        function tick() {
            const diff = target - Date.now();
            if (diff <= 0) {
                out.innerHTML = '<span>00d</span><span>00h</span><span>00m</span>';
                return;
            }
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            out.innerHTML = `<span>${d}d</span><span>${pad(h)}h</span><span>${pad(m)}m</span>`;
        }
        tick();
        setInterval(tick, 60000);
    }

    // ---------- Mobile nav toggle ----------
    function initNavToggle() {
        const toggle = document.querySelector('[data-vf-nav-toggle]');
        const links = document.querySelector('[data-vf-nav-links]');
        if (!toggle || !links) return;
        toggle.addEventListener('click', () => {
            const open = links.classList.toggle('is-open');
            toggle.setAttribute('aria-expanded', String(open));
        });
    }

    // ---------- Sticky nav shadow ----------
    function initStickyNav() {
        const nav = document.querySelector('.almvf-nav');
        if (!nav) return;
        function update() {
            nav.classList.toggle('is-stuck', window.scrollY > 8);
        }
        update();
        window.addEventListener('scroll', update, { passive: true });
    }

    // ---------- FAQ exclusive (close siblings) ----------
    function initFaq() {
        document.querySelectorAll('[data-vf-faq]').forEach((root) => {
            root.querySelectorAll('details').forEach((det) => {
                det.addEventListener('toggle', () => {
                    if (det.open) {
                        root.querySelectorAll('details').forEach((other) => {
                            if (other !== det) other.open = false;
                        });
                    }
                });
            });
        });
    }

    // ---------- Legal TOC active link on scroll ----------
    function initLegalToc() {
        const toc = document.querySelector('.almvf-legal-toc');
        if (!toc) return;
        const links = toc.querySelectorAll('a[href^="#"]');
        if (!links.length) return;
        const targets = [...links].map((a) => document.getElementById(a.getAttribute('href').slice(1))).filter(Boolean);
        if (!targets.length || !('IntersectionObserver' in window)) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                const id = e.target.id;
                const link = toc.querySelector(`a[href="#${id}"]`);
                if (!link) return;
                if (e.isIntersecting) {
                    links.forEach((l) => l.classList.remove('is-active'));
                    link.classList.add('is-active');
                }
            });
        }, { rootMargin: '-30% 0px -60% 0px' });
        targets.forEach((t) => io.observe(t));
    }

    // ---------- Boot ----------
    function boot() {
        initStickyNav();
        initNavToggle();
        initScrollReveal();
        initHeroTyping();
        initCodeLoop();
        initCountUp();
        initTilt();
        initBillingToggle();
        initAeatCountdown();
        initFaq();
        initLegalToc();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
